import {useEffect, useRef, useState} from "react";
import {Link} from "react-router";
import {useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {ArrowLeft} from "lucide-react";
import {v4 as UUID} from "uuid";
import type Campaign from "@/campaigns/model/Campaign.ts";
import type {SaveCampaignRequest} from "@/api/CampaignApi.ts";
import {campaignSchema, type CampaignFormInput, type CampaignFormOutput, slugFromTitle} from "./CampaignFormSchema.ts";
import {Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Textarea} from "@/components/ui/textarea.tsx";
import HiddenField from "@/components/forms/HiddenField.tsx";
import SaveButton, {type SaveState} from "@/components/buttons/SaveButton.tsx";
import {useTemporaryImageFormField} from "@/images/useTemporaryImageFormField.ts";
import ImageUploadField from "@/images/ImageUploadField.tsx";
import {notifyError} from "@/api/ErrorNotification.ts";

export default function CampaignForm({defaultValue, onSubmit}: {
    defaultValue?: Campaign;
    onSubmit: (request: SaveCampaignRequest) => Promise<Campaign>;
}) {
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [error, setError] = useState("");
    const slugEdited = useRef(false);
    const form = useForm<CampaignFormInput, unknown, CampaignFormOutput>({
        resolver: zodResolver(campaignSchema),
        mode: "onBlur",
        defaultValues: defaultValue
            ? {...defaultValue, description: defaultValue.description ?? ""}
            : {id: UUID(), slug: "", title: "", description: "", image: null},
    });
    const title = useWatch({control: form.control, name: "title"});
    const {setValue} = form;
    const imageField = useTemporaryImageFormField({
        form,
        name: "image",
        onUploadStart: () => setError(""),
    });

    useEffect(() => {
        if (!defaultValue && !slugEdited.current) {
            setValue("slug", slugFromTitle(title ?? ""), {shouldDirty: true, shouldValidate: true});
        }
    }, [defaultValue, setValue, title]);

    const onValid = async (data: CampaignFormOutput) => {
        if (imageField.uploading || !data.image) return;
        setSaveState("saving");
        setError("");
        try {
            const saved = await onSubmit({
                id: data.id,
                title: data.title,
                description: data.description,
                imageId: data.image.id,
                slug: data.slug,
            });
            imageField.commit();
            form.reset({...saved, description: saved.description ?? ""});
            setSaveState("success");
        } catch (reason) {
            setSaveState("error");
            notifyError(reason, "Die Kampagne konnte nicht gespeichert werden.");
        }
    };

    const recommendedRatio = 16 / 9;
    const image = imageField.image;
    const ratioDiffers = image && image.height > 0
        && Math.abs(image.width / image.height - recommendedRatio) > 0.1;

    return <main className="min-h-full bg-background p-6 md:p-10">
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onValid, () => setError("Bitte überprüfe die markierten Eingaben."))}
                  className="mx-auto max-w-5xl space-y-6">
                <Link to={defaultValue ? `/admin/campaigns/${defaultValue.slug}` : "/admin/campaigns"}
                      className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="size-4"/>{defaultValue ? "Zur Kampagne" : "Alle Kampagnen"}
                </Link>
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Kampagne verwalten</p>
                        <h1 className="text-3xl font-semibold tracking-tight">
                            {defaultValue ? `${defaultValue.title} bearbeiten` : "Neue Kampagne"}
                        </h1>
                    </div>
                    <SaveButton saveState={imageField.uploading ? "saving" : saveState}
                                disabled={imageField.uploading || saveState === "saving"}/>
                </div>
                {error && <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
                <fieldset disabled={imageField.uploading || saveState === "saving"}
                          className="overflow-hidden rounded-xl border border-border/80 bg-card/90 shadow-lg shadow-black/10">
                    <HiddenField name="id"/>
                    <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,21rem)]">
                        <div className="space-y-5">
                            <FormField control={form.control} name="title" render={({field}) => <FormItem>
                                <FormLabel>Titel</FormLabel>
                                <FormControl><Input {...field} value={field.value ?? ""} placeholder="Titel der Kampagne"/></FormControl>
                                <FormMessage/>
                            </FormItem>}/>
                            <FormField control={form.control} name="slug" render={({field}) => <FormItem>
                                <FormLabel>Slug</FormLabel>
                                <FormControl><Input {...field} value={field.value ?? ""} readOnly={Boolean(defaultValue)}
                                    onChange={event => {
                                        slugEdited.current = true;
                                        field.onChange(event);
                                    }} placeholder="meine-kampagne"/></FormControl>
                                <FormDescription>{defaultValue
                                    ? "Die Kampagnenadresse bleibt nach dem Anlegen unverändert."
                                    : "Wird aus dem Titel vorgeschlagen und kann angepasst werden."}</FormDescription>
                                <FormMessage/>
                            </FormItem>}/>
                            <FormField control={form.control} name="description" render={({field}) => <FormItem>
                                <FormLabel>Beschreibung</FormLabel>
                                <FormControl><Textarea {...field} value={field.value ?? ""} rows={5}/></FormControl>
                                <FormMessage/>
                            </FormItem>}/>
                        </div>
                        <FormField control={form.control} name="image" render={() => <FormItem>
                            <FormLabel>Kampagnenbild</FormLabel>
                            <div className="aspect-video overflow-hidden rounded-lg border border-border bg-surface">
                                <ImageUploadField image={imageField.image} uploading={imageField.uploading}
                                                  onFileSelected={imageField.onFileSelected}
                                                  alt="Kampagnenbild" className="size-full"/>
                            </div>
                            <FormDescription>Bild erforderlich · empfohlenes Seitenverhältnis 16:9</FormDescription>
                            {ratioDiffers && <p className="text-sm text-amber-400">Dieses Bild weicht von 16:9 ab und wird in Karten möglicherweise beschnitten.</p>}
                            <FormMessage/>
                        </FormItem>}/>
                    </div>
                </fieldset>
            </form>
        </Form>
    </main>;
}
